import { sanitizeDataverseRequestBody } from '@/lib/api/dataverse-write-contract'
import { dynamicsApi, type EntityLogicalName } from '@/lib/api/client'
import { assertQuoteEditable } from '@/lib/api/quote-editability'

type PatchOperation = {
  method: 'PATCH'
  entitySet: EntityLogicalName
  id: string
  body: Record<string, unknown>
  /** `If-None-Match: *` — a deterministic CREATE that refuses to update. */
  upsert?: boolean
  /**
   * `If-Match`. A concrete `W/"n"` version also guards against a concurrent edit;
   * `'*'` is an unconditional update, exactly what `updateRecord` sends. One of
   * the two is required: a PATCH with neither header is an upsert to Dataverse,
   * which would silently RESURRECT a record deleted since it was read.
   */
  etag?: string
}

type DeleteOperation = {
  method: 'DELETE'
  entitySet: EntityLogicalName
  id: string
  etag: string
}

export type DataverseChangesetOperation = PatchOperation | DeleteOperation

function batchLine(...lines: string[]) {
  return lines.join('\r\n')
}

const DATAVERSE_ETAG = /^W\/"\d+"$/

export function requireDataverseEtag(record: { '@odata.etag'?: string }) {
  const etag = record['@odata.etag']
  if (!etag || !DATAVERSE_ETAG.test(etag)) throw new Error('Record version is missing. Refresh and try again.')
  return etag
}

function assertChangesetSucceeded(responseBody: unknown, expectedOperations: number) {
  const body = String(responseBody)
  const statuses = [...body.matchAll(/HTTP\/1\.1 (\d{3})/g)].map((match) => Number(match[1]))
  const failedStatus = statuses.find((status) => status < 200 || status >= 300)
  if (failedStatus !== undefined) {
    const rawDetail = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(body)?.[1]
    const detail = rawDetail ? ` ${JSON.parse(`"${rawDetail}"`)}` : ''
    throw new Error(`Dataverse changeset failed. Inner statuses: ${statuses.join(', ')}.${detail}`)
  }
  if (statuses.length !== expectedOperations) {
    throw new Error(
      `Expected ${expectedOperations} Dataverse operation responses, received ${statuses.length}.`
    )
  }
}

export async function executeDataverseChangeset(
  operations: DataverseChangesetOperation[],
  bypassQuoteStateCheck = false
) {
  if (operations.length === 0) return
  assertQuoteEditable(bypassQuoteStateCheck)

  const odataBaseUrl = dynamicsApi.defaults.baseURL
  if (!odataBaseUrl) throw new Error('Dynamics API base URL is missing.')

  const batchBoundary = `batch_${crypto.randomUUID().replaceAll('-', '')}`
  const changesetBoundary = `changeset_${crypto.randomUUID().replaceAll('-', '')}`
  const lines = [
    `--${batchBoundary}`,
    `Content-Type: multipart/mixed; boundary=${changesetBoundary}`,
    '',
  ]

  operations.forEach((operation, index) => {
    const path = `/${operation.entitySet}(${operation.id})`
    lines.push(
      `--${changesetBoundary}`,
      'Content-Type: application/http',
      'Content-Transfer-Encoding: binary',
      `Content-ID: ${index + 1}`,
      '',
      `${operation.method} ${odataBaseUrl}${path} HTTP/1.1`
    )
    if (operation.method === 'PATCH') {
      lines.push('Content-Type: application/json;type=entry')
      if (operation.upsert) {
        lines.push('If-None-Match: *')
      } else {
        if (!operation.etag || !(operation.etag === '*' || DATAVERSE_ETAG.test(operation.etag))) {
          throw new Error('Record version is missing. Refresh and try again.')
        }
        lines.push(`If-Match: ${operation.etag}`)
      }
      lines.push('', JSON.stringify(sanitizeDataverseRequestBody(path, operation.body)))
    } else {
      if (!DATAVERSE_ETAG.test(operation.etag)) {
        throw new Error('Record version is missing. Refresh and try again.')
      }
      lines.push(`If-Match: ${operation.etag}`, '')
    }
  })
  lines.push(`--${changesetBoundary}--`, `--${batchBoundary}--`, '')

  const response = await dynamicsApi.post<string>('/$batch', batchLine(...lines), {
    headers: {
      Accept: 'application/json',
      'Content-Type': `multipart/mixed; boundary=${batchBoundary}`,
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
    },
    transformResponse: [(data: unknown) => data],
    _retryable: false,
  })
  assertChangesetSucceeded(response.data, operations.length)
}
